import React from "react";
import "@/App.css";
import "@/portal.css";
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Outlet, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import AdminClients from "@/pages/AdminClients";
import AdminInbox from "@/pages/AdminInbox";
import Leads from "@/pages/Leads";
import BusinessData from "@/pages/BusinessData";
import Photos from "@/pages/Photos";
import Placeholder from "@/pages/Placeholder";
import Legal from "@/pages/Legal";
import Account from "@/pages/Account";
import AdminAudit from "@/pages/AdminAudit";
import { NavigationGuardProvider } from "@/contexts/NavigationGuardContext";

function RootRedirect() {
    const { user } = useAuth();
    if (user === null) {
        return (
            <div className="flex h-screen items-center justify-center bg-[#0a0a0a]" data-testid="root-loading">
                <div className="flex items-center gap-2 text-zinc-500 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 animate-pulse" />
                    Loading
                </div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
}

function AppProviders() {
    return <AuthProvider><NavigationGuardProvider><Outlet /></NavigationGuardProvider></AuthProvider>;
}

const router = createBrowserRouter(createRoutesFromElements(
                    <Route element={<AppProviders />}>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/privacy" element={<Legal />} />
                        <Route path="/terms" element={<Legal />} />
                        <Route path="/dpa" element={<Legal />} />

                        {/* Protected app shell */}
                        <Route
                            element={
                                <ProtectedRoute>
                                    <Layout />
                                </ProtectedRoute>
                            }
                        >
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/leads" element={<Leads />} />
                            <Route path="/account" element={<Account />} />
                            <Route path="/settings" element={<BusinessData />} />
                            <Route
                                path="/content"
                                element={<Navigate to="/settings" replace />}
                            />
                            <Route
                                path="/photos"
                                element={<Photos />}
                            />
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute role="admin">
                                        <Admin />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/clients"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminClients />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/audit"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminAudit />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin/inbox"
                                element={
                                    <ProtectedRoute role="admin">
                                        <AdminInbox />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
));

function App() {
    return <div className="App"><RouterProvider router={router} /></div>;
}

export default App;
